import { useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import {
  bulkReceiveStock,
  BulkReceiveStockRequest,
} from "@/api/stock-movement-api";

interface UseBulkReceiveStockOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function useBulkReceiveStock(options?: UseBulkReceiveStockOptions) {
  const handleBulkReceiveStock = useCallback(
    async (data: Record<string, number | undefined>) => {
      try {
        // Filter out variants with quantity 0 and convert to API format
        const requests: BulkReceiveStockRequest[] = Object.entries(data)
          .filter(([_, quantity]) => quantity && quantity > 0)
          .map(([variantId, quantity]) => ({
            variantId: variantId,
            quantity: quantity!,
          }));

        if (requests.length === 0) {
          toast({
            title: "No stock to receive",
            description:
              "Please enter quantities greater than 0 for at least one variant.",
            variant: "destructive",
          });
          return;
        }

        await bulkReceiveStock(requests);

        toast({
          title: "Success",
          description: `Successfully received stock for ${requests.length} variant(s).`,
        });

        // Call onSuccess callback if provided
        if (options?.onSuccess) {
          options.onSuccess();
        }
      } catch (error) {
        console.error("Error bulk receiving stock:", error);
        toast({
          title: "Error",
          description: "Failed to receive stock. Please try again.",
          variant: "destructive",
        });

        // Call onError callback if provided
        if (options?.onError) {
          options.onError(error);
        }
      }
    },
    [options]
  );

  return { handleBulkReceiveStock };
}
